import React from "react";
import { Box, Stack, Tooltip } from "@mui/material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import TabletMacIcon from "@mui/icons-material/TabletMac";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import LaptopChromebookIcon from "@mui/icons-material/LaptopChromebook";
import ComputerIcon from "@mui/icons-material/Computer";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import LanguageIcon from "@mui/icons-material/Language";
import PaletteIcon from "@mui/icons-material/Palette";
import { useTranslation } from "react-i18next";
import {
  getClientEnvironmentAppThemeLabel,
  getClientEnvironmentAppVersionLabel,
  getClientEnvironmentBrowserLabel,
  getClientEnvironmentDeviceLabel,
  normalizeAppTheme,
  normalizeAppVersion,
  normalizeBrowserName,
  normalizeDeviceType,
} from "../utils/clientEnvironment";

const SIZE_MAP = {
  small: 16,
  medium: 18,
  default: 20,
};

function iconBoxSx(sizePx) {
  return {
    width: sizePx + 6,
    height: sizePx + 6,
    borderRadius: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "text.secondary",
    bgcolor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
  };
}

function DeviceGlyph({ deviceType, fontSize }) {
  const type = normalizeDeviceType(deviceType);
  const sx = { fontSize };
  switch (type) {
    case "iphone":
      return <PhoneIphoneIcon sx={sx} />;
    case "ipad":
      return <TabletMacIcon sx={sx} />;
    case "android":
      return <PhoneAndroidIcon sx={sx} />;
    case "windows":
      return <DesktopWindowsIcon sx={sx} />;
    case "macos":
      return <LaptopMacIcon sx={sx} />;
    case "chromebook":
      return <LaptopChromebookIcon sx={sx} />;
    case "linux":
      return <ComputerIcon sx={sx} />;
    default:
      return <DevicesOtherIcon sx={sx} />;
  }
}

function BrowserGlyph({ browserName, fontSize }) {
  const type = normalizeBrowserName(browserName);
  const size = fontSize;
  if (type === "unknown") {
    return <LanguageIcon sx={{ fontSize: size }} />;
  }

  const common = { width: size, height: size, display: "block" };
  switch (type) {
    case "chrome":
      return (
        <svg viewBox="0 0 24 24" aria-hidden style={common}>
          <circle cx="12" cy="12" r="10" fill="#4285F4" />
          <circle cx="12" cy="12" r="4.2" fill="#fff" />
          <path d="M12 2a10 10 0 0 1 8.66 5H12z" fill="#EA4335" />
          <path d="M3.34 7A10 10 0 0 0 2 12h10z" fill="#34A853" />
          <path d="M4.34 17A10 10 0 0 0 12 22V12H2a10 10 0 0 0 2.34 5z" fill="#FBBC05" />
        </svg>
      );
    case "safari":
      return (
        <svg viewBox="0 0 24 24" aria-hidden style={common}>
          <circle cx="12" cy="12" r="10" fill="#0fb5ff" />
          <polygon points="12,6 15.5,15 12,12.5 8.5,15" fill="#fff" />
          <polygon points="12,6 15.5,15 12,12.5" fill="#ff3b30" opacity="0.95" />
        </svg>
      );
    case "firefox":
      return (
        <svg viewBox="0 0 24 24" aria-hidden style={common}>
          <circle cx="12" cy="12" r="10" fill="#ff7139" />
          <circle cx="10" cy="10" r="4.5" fill="#ffcd0a" />
        </svg>
      );
    case "edge":
      return (
        <svg viewBox="0 0 24 24" aria-hidden style={common}>
          <circle cx="12" cy="12" r="10" fill="#0078d4" />
          <path d="M7 12c0-2.8 2.2-5 5-5 1.4 0 2.6.6 3.5 1.5C14 7.5 12.6 7 11 7 7.7 7 5 9.7 5 13s2.7 6 6 6c2.2 0 4.1-1.2 5.1-3H11c-1.1 0-2-.9-2-2z" fill="#50e6ff" />
        </svg>
      );
    case "opera":
      return (
        <svg viewBox="0 0 24 24" aria-hidden style={common}>
          <circle cx="12" cy="12" r="10" fill="#ff1b2d" />
          <ellipse cx="12" cy="12" rx="3.2" ry="7" fill="#fff" />
        </svg>
      );
    case "samsung":
      return (
        <svg viewBox="0 0 24 24" aria-hidden style={common}>
          <rect x="3" y="3" width="18" height="18" rx="4" fill="#1428a0" />
          <text x="12" y="15.5" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700" fontFamily="Arial,sans-serif">
            S
          </text>
        </svg>
      );
    default:
      return <LanguageIcon sx={{ fontSize: size }} />;
  }
}

function EnvironmentIcon({ kind, value, sizePx, t }) {
  const normalized = kind === "device" ? normalizeDeviceType(value) : normalizeBrowserName(value);
  if (normalized === "unknown") return null;

  const label =
    kind === "device"
      ? getClientEnvironmentDeviceLabel(normalized, t)
      : getClientEnvironmentBrowserLabel(normalized, t);

  return (
    <Tooltip title={label}>
      <Box component="span" sx={iconBoxSx(sizePx)} aria-label={label}>
        {kind === "device" ? (
          <DeviceGlyph deviceType={normalized} fontSize={sizePx} />
        ) : (
          <BrowserGlyph browserName={normalized} fontSize={sizePx} />
        )}
      </Box>
    </Tooltip>
  );
}

function AppVersionBadge({ version, sizePx, t }) {
  const normalized = normalizeAppVersion(version);
  if (!normalized) return null;
  const label = getClientEnvironmentAppVersionLabel(normalized, t);

  return (
    <Tooltip title={label}>
      <Box
        component="span"
        aria-label={label}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          px: 0.6,
          minWidth: sizePx + 10,
          height: sizePx + 6,
          borderRadius: 1,
          fontSize: Math.max(10, sizePx - 5),
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: 0.01,
          fontVariantNumeric: "tabular-nums",
          color: "text.secondary",
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          border: 1,
          borderColor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
        }}
      >
        v{normalized}
      </Box>
    </Tooltip>
  );
}

function AppThemeBadge({ themeKey, sizePx, t, tThemes }) {
  const normalized = normalizeAppTheme(themeKey);
  if (!normalized) return null;
  const label = getClientEnvironmentAppThemeLabel(normalized, t, tThemes);
  const shortName = tThemes(normalized, { defaultValue: normalized });

  return (
    <Tooltip title={label}>
      <Box
        component="span"
        aria-label={label}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.35,
          flexShrink: 0,
          px: 0.55,
          maxWidth: 120,
          height: sizePx + 6,
          borderRadius: 1,
          fontSize: Math.max(10, sizePx - 6),
          fontWeight: 600,
          lineHeight: 1,
          color: "text.secondary",
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          border: 1,
          borderColor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <PaletteIcon sx={{ fontSize: sizePx - 2, flexShrink: 0 }} />
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {shortName}
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * Ícones de dispositivo, navegador, versão e tema da plataforma (presença ao vivo e uso do produto).
 */
export default function ClientEnvironmentIcons({
  deviceType,
  browserName,
  appVersion,
  appTheme,
  deviceTypes,
  browserNames,
  appVersions,
  appThemes,
  size = "medium",
  gap = 0.5,
}) {
  const { t } = useTranslation("app");
  const { t: tThemes } = useTranslation("themes");
  const sizePx = SIZE_MAP[size] || SIZE_MAP.medium;

  const devices = (deviceTypes?.length ? deviceTypes : deviceType ? [deviceType] : [])
    .map(normalizeDeviceType)
    .filter((v, i, arr) => v !== "unknown" && arr.indexOf(v) === i);

  const browsers = (browserNames?.length ? browserNames : browserName ? [browserName] : [])
    .map(normalizeBrowserName)
    .filter((v, i, arr) => v !== "unknown" && arr.indexOf(v) === i);

  const versions = (appVersions?.length ? appVersions : appVersion ? [appVersion] : [])
    .map(normalizeAppVersion)
    .filter((v, i, arr) => v && arr.indexOf(v) === i);

  const themeKeys = (appThemes?.length ? appThemes : appTheme ? [appTheme] : [])
    .map(normalizeAppTheme)
    .filter((v, i, arr) => v && arr.indexOf(v) === i);

  if (!devices.length && !browsers.length && !versions.length && !themeKeys.length) return null;

  return (
    <Stack direction="row" spacing={gap} alignItems="center" component="span" sx={{ verticalAlign: "middle" }}>
      {devices.map((dt) => (
        <EnvironmentIcon key={`d-${dt}`} kind="device" value={dt} sizePx={sizePx} t={t} />
      ))}
      {browsers.map((bn) => (
        <EnvironmentIcon key={`b-${bn}`} kind="browser" value={bn} sizePx={sizePx} t={t} />
      ))}
      {versions.map((ver) => (
        <AppVersionBadge key={`v-${ver}`} version={ver} sizePx={sizePx} t={t} />
      ))}
      {themeKeys.map((th) => (
        <AppThemeBadge key={`t-${th}`} themeKey={th} sizePx={sizePx} t={t} tThemes={tThemes} />
      ))}
    </Stack>
  );
}
